import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { useAuth } from "../lib/auth";
import { PageHeader, NBCard, NBButton, NBInput, NBSelect, NBTextarea, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, Gift, Eye, EyeOff, ShoppingBag, Check } from "lucide-react";

const PRIORITIES = ["low", "medium", "high"];

export default function Wishlists() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", url: "", price_estimate: "", priority: "medium", member_id: "" });

  const load = async () => {
    const [w, m] = await Promise.all([api.get("/api/wishlists"), api.get("/api/members")]);
    setItems(w.data);
    setMembers(m.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/wishlists", {
        title: form.title,
        description: form.description,
        url: form.url,
        price_estimate: form.price_estimate ? Number(form.price_estimate) : null,
        priority: form.priority,
        member_id: form.member_id || null,
      });
      toast.success("Added!");
      setOpen(false);
      setForm({ title: "", description: "", url: "", price_estimate: "", priority: "medium", member_id: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const claim = async (item) => {
    try {
      await api.post(`/api/wishlists/${item.id}/claim`, { claim: !item.claimed_by });
      toast.success(item.claimed_by ? "Released" : "Claimed! Don't tell them 🤫");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const purchased = async (item) => {
    if (!window.confirm("Mark this as purchased? It will move to the bottom.")) return;
    await api.post(`/api/wishlists/${item.id}/purchased`);
    toast.success("Marked purchased 🎁");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this wish?")) return;
    await api.delete(`/api/wishlists/${id}`);
    load();
  };

  const memberById = (id) => members.find((m) => m.id === id);
  const filtered = filter === "all" ? items : items.filter((i) => i.member_id === filter);

  return (
    <div>
      <PageHeader
        title="Wishlists & Gifts"
        subtitle="Wish for things, claim a gift secretly, no spoilers."
        color="#E9B5A0"
        action={<NBButton testId="add-wish-btn" color="#E9B5A0" onClick={() => setOpen(true)}><Plus strokeWidth={2.4} className="w-4 h-4 inline mr-1" />Wish</NBButton>}
      />

      <div className="flex gap-2 flex-wrap mb-6" data-testid="member-filter">
        <button onClick={() => setFilter("all")} data-testid="filter-all"
          className="nb-btn px-3 py-1.5 text-xs"
          style={filter === "all" ? { background: "var(--hh-ink)", color: "var(--hh-card)" } : {}}>
          Everyone
        </button>
        {members.map((m) => (
          <button key={m.id} onClick={() => setFilter(m.id)} data-testid={`filter-${m.id}`}
            className="nb-btn px-3 py-1.5 text-xs flex items-center gap-1.5"
            style={filter === m.id ? { background: m.avatar_color, color: "var(--hh-ink)" } : {}}>
            <span className="w-2 h-2 rounded-full" style={{ background: m.avatar_color }} />
            {m.name}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No wishes yet" subtitle="Tap '+ Wish' to add the first one." icon="🎁" action={<NBButton color="#E9B5A0" onClick={() => setOpen(true)}>Add a wish</NBButton>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item) => {
            const recipient = memberById(item.member_id);
            const isOwn = item.member_id === user?.member?.id;
            const isClaimed = item.is_claimed;
            const claimedByMe = item.claimed_by === user?.member?.id;
            return (
              <div key={item.id} className="nb-card !p-5 flex flex-col" data-testid={`wish-${item.id}`}
                style={item.is_purchased ? { opacity: 0.55 } : {}}>
                <div className="flex items-start gap-2 mb-2">
                  <Gift strokeWidth={2.2} className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--hh-terracotta)" }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold leading-tight" style={{ fontFamily: "var(--hh-display)" }}>{item.title}</div>
                    {recipient && (
                      <div className="text-[11px] mt-1 flex items-center gap-1" style={{ color: "var(--hh-ink-soft)" }}>
                        for <span className="w-2 h-2 rounded-full" style={{ background: recipient.avatar_color }} /> {recipient.name}
                      </div>
                    )}
                  </div>
                  <button onClick={() => remove(item.id)} className="nb-btn p-1.5 opacity-40 hover:opacity-100" data-testid={`delete-wish-${item.id}`}><Trash2 strokeWidth={2.4} className="w-3.5 h-3.5" /></button>
                </div>
                {item.description && <p className="text-sm leading-relaxed line-clamp-2 mb-2" style={{ color: "var(--hh-ink-soft)" }}>{item.description}</p>}
                <div className="flex flex-wrap gap-1.5 text-[11px] mb-3">
                  <span className="px-2 py-0.5 rounded-full" style={{ background: "var(--hh-card-soft)", border: "1px solid var(--hh-line)" }}>{item.priority}</span>
                  {item.price_estimate != null && <span className="px-2 py-0.5 rounded-full" style={{ background: "var(--hh-honey-soft)", border: "1px solid var(--hh-line)" }}>~${Number(item.price_estimate).toFixed(0)}</span>}
                  {item.url && <a href={item.url} target="_blank" rel="noreferrer" className="px-2 py-0.5 rounded-full" style={{ background: "var(--hh-dusk-soft)", border: "1px solid var(--hh-line)" }}>link →</a>}
                </div>
                <div className="mt-auto pt-3 flex items-center gap-2" style={{ borderTop: "1px solid var(--hh-line)" }}>
                  {isOwn ? (
                    <span className="text-[11px] flex items-center gap-1" style={{ color: "var(--hh-ink-soft)" }}>
                      <EyeOff strokeWidth={2.4} className="w-3 h-3" /> hidden until your birthday
                    </span>
                  ) : item.is_purchased ? (
                    <span className="text-[11px] font-semibold flex items-center gap-1" style={{ color: "var(--hh-sage)" }}>
                      <Check strokeWidth={2.4} className="w-3 h-3" /> purchased
                    </span>
                  ) : (
                    <>
                      <button onClick={() => claim(item)} data-testid={`claim-wish-${item.id}`}
                        className="nb-btn px-3 py-1.5 text-xs"
                        style={isClaimed && claimedByMe ? { background: "var(--hh-sage-soft)" } : isClaimed ? { opacity: 0.4 } : {}}
                        disabled={isClaimed && !claimedByMe}>
                        {isClaimed && claimedByMe ? "✓ You claimed" : isClaimed ? `claimed by ${item.claimed_by_name}` : "Claim"}
                      </button>
                      {claimedByMe && (
                        <button onClick={() => purchased(item)} className="nb-btn px-3 py-1.5 text-xs" data-testid={`purchased-${item.id}`}>
                          <ShoppingBag strokeWidth={2.4} className="w-3 h-3 inline mr-1" />Mark bought
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="wish-modal">
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>New wish</h2>
              <button onClick={() => setOpen(false)} className="nb-btn p-1.5"><X strokeWidth={2.4} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="wish-title" required placeholder="Title (e.g. Lego Death Star)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <NBTextarea testId="wish-desc" placeholder="Why this? Size, color, brand…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <NBInput testId="wish-url" type="url" placeholder="Link (optional)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <NBInput testId="wish-price" type="number" step="0.01" min="0" placeholder="$ estimate" value={form.price_estimate} onChange={(e) => setForm({ ...form, price_estimate: e.target.value })} />
                <NBSelect testId="wish-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                </NBSelect>
              </div>
              <NBSelect testId="wish-member" value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })}>
                <option value="">For me ({user?.member?.name})</option>
                {members.filter((m) => m.id !== user?.member?.id).map((m) => <option key={m.id} value={m.id}>For {m.name}</option>)}
              </NBSelect>
              <NBButton testId="submit-wish" type="submit" color="#E9B5A0" className="w-full">Save</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
